import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  AffiliateApplication,
  AffiliateApplicationStatus,
  UpdateAffiliateApplicationDto,
} from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformAffiliateApplication } from '../infra/mappers';

interface UpdateAffiliateApplicationParams {
  projectId: string;
  applicationId: string;
  currentStatus: AffiliateApplicationStatus;
  dto: UpdateAffiliateApplicationDto;
}

interface UseUpdateAffiliateApplicationResult {
  updateApplication: (params: UpdateAffiliateApplicationParams) => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useUpdateAffiliateApplication = (): UseUpdateAffiliateApplicationResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateApplication, Error, UpdateAffiliateApplicationParams> = useMutation({
    mutationFn: async ({ projectId, applicationId, dto }: UpdateAffiliateApplicationParams) => {
      const response = await affiliateApplicationService.updateApplication(projectId, applicationId, dto);
      return transformAffiliateApplication(response);
    },
    onSuccess: (data, variables) => {
      toast.success('Application updated successfully');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-applications', variables.projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['affiliate-application', variables.projectId, variables.applicationId],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update application');
    },
  });

  return {
    updateApplication: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
